/**
 * ============================================================================
 * VINPOP — Purchase Filter
 * ============================================================================
 *
 * Filters collection products based on customer purchase history.
 * Adds a toggle button that shows/hides already-purchased wines,
 * using data from the account-wine-data.js.liquid script.
 *
 * Loaded from: layout/theme.liquid (global, deferred)
 * Depends on: account-wine-data.js.liquid
 * ============================================================================
 */

document.addEventListener('DOMContentLoaded', function() {
  const pathname = window.location.pathname || '';
  const isMyWinePlaylistPage = /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/collections\/my-wine-playlist\/?$/i.test(pathname);

  if (isMyWinePlaylistPage) {
    return;
  }

  if (window.vinpopMyWinePlaylistFilterActive) {
    return;
  }

  const BADGE_SELECTOR = [
    '[data-already-purchased="true"]',
    '.badge-already-purchased',
    '[class*="already-purchased-sticker"]',
    '[class*="sticker-already-purchased"]'
  ].join(', ');

  const customerIdMeta = document.querySelector('meta[name="customer-id"]');
  const customerId = customerIdMeta ? customerIdMeta.getAttribute('content') : '';
  const isLoggedIn = !!customerId;

  let listenersBound = false;
  let purchasedIdsLoaded = false;
  let purchasedIdsPromise = null;
  let purchasedProductIds = new Set();

  if (window.vinpopPurchasedProductIds instanceof Set) {
    purchasedProductIds = window.vinpopPurchasedProductIds;
    purchasedIdsLoaded = true;
  } else {
    window.vinpopPurchasedProductIds = purchasedProductIds;
  }

  // Add CSS for hiding/showing products
  const style = document.createElement('style');
  style.textContent = `
    .purchase-filter-hidden { display: none !important; }
    .purchase-filter-visible { display: block !important; }
  `;
  document.head.appendChild(style);

  function applyPurchaseFilter() {
    if (!isLoggedIn) {
      showAllProducts();
      return;
    }

    const purchasedChecked = document.querySelector('#filter-purchased:checked');
    const notPurchasedChecked = document.querySelector('#filter-not-purchased:checked');

    // Don't filter if no checkboxes are selected
    if (!purchasedChecked && !notPurchasedChecked) {
      showAllProducts();
      return;
    }

    console.log('🎯 Applying purchase filter:', {
      purchased: !!purchasedChecked,
      notPurchased: !!notPurchasedChecked
    });

    // Check if we're in taste profile mode
    const urlParams = new URLSearchParams(window.location.search);
    const inTasteProfileMode = urlParams.get('taste_profile') === 'true';

    // Find all product cards, but only those visible from quiz if in taste profile mode
    let productCards;
    if (inTasteProfileMode) {
      // Only filter products that are NOT hidden by the quiz
      productCards = document.querySelectorAll('[data-product-id]:not(.taste-hidden)');
      console.log('🍷 Taste profile mode: filtering only quiz-recommended wines');
    } else {
      productCards = document.querySelectorAll('[data-product-id]');
    }
    
    let visibleCount = 0;

    productCards.forEach(card => {
      const productId = String(card.getAttribute('data-product-id') || '');
      const hasBadge = isProductPurchased(card, productId);
      let shouldShow = false;

      // Apply filter logic
      if (purchasedChecked && hasBadge) shouldShow = true;
      if (notPurchasedChecked && !hasBadge) shouldShow = true;

      // Show/hide product
      const parentListItem = card.closest('li');
      if (shouldShow) {
        card.classList.remove('purchase-filter-hidden');
        card.classList.add('purchase-filter-visible');
        if (parentListItem) {
          parentListItem.classList.remove('purchase-filter-hidden');
          parentListItem.classList.add('purchase-filter-visible');
        }
        visibleCount++;
      } else {
        card.classList.add('purchase-filter-hidden');
        card.classList.remove('purchase-filter-visible');
        if (parentListItem) {
          parentListItem.classList.add('purchase-filter-hidden');
          parentListItem.classList.remove('purchase-filter-visible');
        }
      }
    });

    console.log(`✅ Filter applied: ${visibleCount} products visible`);
    updateProductCount(visibleCount, purchasedChecked || notPurchasedChecked);
  }

  function isProductPurchased(card, productId) {
    if (!productId) {
      return false;
    }
    if (purchasedProductIds && purchasedProductIds.has(productId)) {
      return true;
    }
    if (window.vinpopPurchasedProductIds && window.vinpopPurchasedProductIds.has(productId)) {
      return true;
    }
    if (card.getAttribute('data-already-purchased') === 'true') {
      return true;
    }
    return !!card.querySelector(BADGE_SELECTOR);
  }

  async function loadPurchasedProductIds() {
    if (!isLoggedIn) {
      return [];
    }

    if (window.vinpopPurchasedProductIds instanceof Set) {
      purchasedProductIds = window.vinpopPurchasedProductIds;
      purchasedIdsLoaded = true;
      return Array.from(purchasedProductIds);
    }

    if (purchasedIdsPromise) {
      return purchasedIdsPromise;
    }

    purchasedIdsPromise = Promise.resolve(Array.from(purchasedProductIds || []));
    purchasedIdsLoaded = true;

    return purchasedIdsPromise;
  }

  function showAllProducts() {
    const productCards = document.querySelectorAll('[data-product-id]');
    productCards.forEach(card => {
      const parentListItem = card.closest('li');
      card.classList.remove('purchase-filter-hidden', 'purchase-filter-visible');
      if (parentListItem) {
        parentListItem.classList.remove('purchase-filter-hidden', 'purchase-filter-visible');
      }
    });
    updateProductCount(null, false);
  }

  function updateProductCount(count, isFiltering) {
    const countElements = document.querySelectorAll('[data-results-count], .collection-count, .results-count');
    countElements.forEach(element => {
      if (isFiltering && count !== null) {
        const originalText = element.getAttribute('data-original-text');
        if (!originalText) {
          element.setAttribute('data-original-text', element.textContent);
        }
        element.textContent = `Showing ${count} filtered products`;
      } else if (element.getAttribute('data-original-text')) {
        element.textContent = element.getAttribute('data-original-text');
      }
    });
  }

  // Set up event listeners with mutual exclusivity
  function setupFilterListeners() {
    if (listenersBound) return;

    const purchasedCheckbox = document.querySelector('#filter-purchased');
    const notPurchasedCheckbox = document.querySelector('#filter-not-purchased');

    if (purchasedCheckbox) {
      if (purchasedCheckbox.dataset.vinpopPurchaseBound !== 'true') {
        purchasedCheckbox.dataset.vinpopPurchaseBound = 'true';
        purchasedCheckbox.addEventListener('change', function() {
          if (this.checked && notPurchasedCheckbox) {
            notPurchasedCheckbox.checked = false;
          }
          applyPurchaseFilter();
        });
      }
    }

    if (notPurchasedCheckbox) {
      if (notPurchasedCheckbox.dataset.vinpopPurchaseBound !== 'true') {
        notPurchasedCheckbox.dataset.vinpopPurchaseBound = 'true';
        notPurchasedCheckbox.addEventListener('change', function() {
          if (this.checked && purchasedCheckbox) {
            purchasedCheckbox.checked = false;
          }
          applyPurchaseFilter();
        });
      }
    }

    listenersBound = true;
  }

  // Initialize when filters are available
  function initializeFilter() {
    if (!isLoggedIn) {
      console.log('ℹ️ Purchase Filter disabled - customer not logged in');
      return;
    }

    const filterContainer = document.querySelector('#filter-purchased, #filter-not-purchased');
    if (filterContainer) {
      loadPurchasedProductIds().then(() => {
        setupFilterListeners();
        applyPurchaseFilter();
        console.log('✅ Purchase Filter Ready');
      });
    } else {
      // Retry after a short delay (filters might load asynchronously)
      setTimeout(initializeFilter, 500);
    }
  }

  // Watch for dynamic content changes
  const observer = new MutationObserver(function(mutations) {
    const hasProductChanges = mutations.some(mutation =>
      Array.from(mutation.addedNodes).some(node =>
        node.nodeType === 1 &&
        (node.hasAttribute?.('data-product-id') || node.querySelector?.('[data-product-id]'))
      )
    );

    const hasFilterChanges = mutations.some(mutation =>
      Array.from(mutation.addedNodes).some(node =>
        node.nodeType === 1 &&
        (node.id === 'filter-purchased' ||
          node.id === 'filter-not-purchased' ||
          node.querySelector?.('#filter-purchased, #filter-not-purchased'))
      )
    );

    if (hasFilterChanges) {
      listenersBound = false;
      setupFilterListeners();
      if (purchasedIdsLoaded) {
        applyPurchaseFilter();
      }
    }

    if (hasProductChanges || hasFilterChanges) {
      setTimeout(applyPurchaseFilter, 100);
    }
  });

  // Start observing
  const productContainer = document.querySelector('#facets-results, #product-grid, .collection-products, .products-grid');
  if (productContainer) {
    observer.observe(productContainer, {
      childList: true,
      subtree: true
    });
  }

  // Initialize
  initializeFilter();
});
